import React from 'react';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import BugReportIcon from '@mui/icons-material/BugReport';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import LaunchIcon from '@mui/icons-material/Launch';
import PictureInPictureIcon from '@mui/icons-material/PictureInPicture';
import { handleWindowOpen } from '../../Helpers/WindowOpenHelper';
import { MenuItem } from './LeftSideMenu.types';
import { Auth0Verrific } from 'verrific-plus-schema';
import { DENTAL_OFFICE_SETTINGS_URL, MANAGEMENT_URL, PAYMENT_SETUP_URL, SCHEDULE_URL,  } from '../../Routes/RoutingTable';
import { usePlanType } from '../../Helpers/Hooks/usePlanType';


/**
 * Check if current path matches the given test part
 * @param testPart - Part of the path to test
 * @param direct - Whether to check for exact match
 * @returns Whether the current path matches the test part
 */
export const testCurrentPath = (testPart: string, direct?: boolean): boolean => direct 
  ? window.location.href.endsWith(testPart) && window.location.pathname === testPart 
  : window.location.href.includes(testPart);

/**
 * Get menu items configuration based on plan type and integrations
 * @param isDOScheduleIntegrated - Whether Dental Office Schedule is integrated
 * @param handleBugReportOpen - Callback function for bug report dialog
 * @returns List of menu items
 */
export const getMenuItems = (
  handleBugReportOpen: (event?: React.MouseEvent<HTMLLIElement>) => void
): MenuItem[] => {
  const { planType } = usePlanType();
  // Check if the current path is related to management
  const isManagement = location.pathname.startsWith(MANAGEMENT_URL);

  // Check if User have access to the schedule view
  const isDOScheduleIntegrated = Boolean(Auth0Verrific.getIntegrationData()?.subdomain) && planType !== 'freemium';
  
  // Check if the current path is related to schedule
  const isSchedule = location.pathname.startsWith(SCHEDULE_URL);
  
  // User permissions check
  const hasCustomizationAccess = Auth0Verrific.isDentalOfficeAuthorized() && 
    (
      Auth0Verrific.isStaffManager() ||
      Auth0Verrific.isGroupManager()
    );

  // Check if billing settings should be available
  const isBillable = ['auto', 'locked', undefined].includes(Auth0Verrific.getPaymentType()) && 
    planType !== 'freemium';

  const isPayment = location.pathname.startsWith(PAYMENT_SETUP_URL);
  
  // Determine whether to show billing settings
  const showBillingSettings = Auth0Verrific.isDentalOfficeAuthorized() && 
    !Auth0Verrific.isDODuringRegistration() &&
    (
      Auth0Verrific.isStaffManager() ||
      Auth0Verrific.isGroupManager()
    ) &&
    !isPayment && isBillable ;

  // Determine whether to show user settings section
  const shouldShowUserSetting = showBillingSettings || hasCustomizationAccess;

  return ([
    { 
      icon: <FiberNewIcon />, 
      text: 'New Verification', 
      turnedOn: true, 
      disabled: testCurrentPath('/verification', true), 
      action: () => handleWindowOpen(window.open('/verification', '_blank')),
      actionIcon: <LaunchIcon />
    },
    { 
      icon: <ContentPasteSearchIcon />, 
      text: 'Plan Search', 
      turnedOn: true, 
      disabled: testCurrentPath('/plan'), 
      action: () => handleWindowOpen(window.open('/plan', '_blank')),
      actionIcon: <LaunchIcon />
    },
    {
      icon: <SettingsIcon /> ,
      text: 'Management',
      turnedOn: Auth0Verrific.isCompanyAuthorized(),
      disabled: isManagement,
      action: () => handleWindowOpen(window.open('/management', '_blank')),
    },
    { 
      icon: <CalendarMonthIcon />, 
      text: 'Appointment Schedule', 
      turnedOn: isDOScheduleIntegrated, 
      disabled: isSchedule, 
      action: () => handleWindowOpen(window.open('/schedule', '_blank')),
      actionIcon: <LaunchIcon />
    },
    { 
      icon: <HistoryIcon />, 
      text: 'Verifications History', 
      turnedOn: Auth0Verrific.isDentalOfficeAuthorized() && 
                !Auth0Verrific.isDODuringRegistration(), 
      disabled: testCurrentPath('/past-verifications'), 
      action: () => handleWindowOpen(window.open('/past-verifications', '_blank')),
      actionIcon: <LaunchIcon />
    },
    {
      icon: <SettingsIcon />, 
      turnedOn: shouldShowUserSetting, 
      disabled: false,
      subItems: [
        { 
          text: 'Form Customization',
          turnedOn: hasCustomizationAccess,
          action: () => handleWindowOpen(window.open('/customization/verification', '_blank')), 
          disabled: testCurrentPath('/customization/verification'),
          actionIcon: <LaunchIcon />
        },
        { 
          text: 'Billing Settings',
          turnedOn: showBillingSettings,
          action: () => handleWindowOpen(window.open('/payment-settings', '_blank')), 
          disabled: testCurrentPath('/payment-settings'),
          actionIcon: <LaunchIcon />
        },
        { 
          text: 'Dental Office Settings',
          turnedOn: hasCustomizationAccess,
          action: () => handleWindowOpen(window.open(DENTAL_OFFICE_SETTINGS_URL, '_blank')), 
          disabled: testCurrentPath(DENTAL_OFFICE_SETTINGS_URL),
          actionIcon: <LaunchIcon />
        }
      ] 
    },
    { 
      icon: <MenuBookIcon />, 
      text: 'User Guide', 
      turnedOn: true, 
      disabled: testCurrentPath('/user-guide'), 
      action: () => handleWindowOpen(window.open('/user-guide', '_blank')),
      actionIcon: <LaunchIcon />
    },
    { 
      icon: <BugReportIcon />, 
      text: 'Bug report', 
      turnedOn: true, 
      disabled: false, 
      action: (event?: React.MouseEvent<HTMLLIElement>) => handleBugReportOpen(event),
      actionIcon: <PictureInPictureIcon />
    },
  ]);
};
